import React, { useEffect, useState } from "react";
import "../orderList/orderList.css";
import { userRequest } from "../../requestMethods";
import { useSelector } from "react-redux";
import moment from "moment";
import {
    CheckCircle,
    Cancel as XCircle,
    Visibility as Eye,
    Close,
    Delete,
} from "@material-ui/icons";

const Skip50Requests = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const superadmin = currentUser?.isSuperAdmin;
    const outright = currentUser?.type === "outright";
    const manager = currentUser?.type === "manager";
    const isSpecialSkip50User =
        currentUser?._id === "693a87b313df78d75f75c484";

    const canApprove = superadmin || outright || manager || isSpecialSkip50User;

    const getPrincipleParam = () => {
        if (superadmin) {
            return undefined;
        } else if (outright || manager) {
            return currentUser?._id;
        } else {
            return currentUser?.principle;
        }
    };

    useEffect(() => {
        if (canApprove) {
            fetchRequests();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filterStatus,
        canApprove,
        currentUser?._id,
        currentUser?.principle,
        currentUser?.type,
        superadmin,
    ]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const principleParam = getPrincipleParam();
            let url = `/skip50Requests?status=${filterStatus}`;
            if (principleParam) {
                url += `&principle=${principleParam}`;
            }
            const res = await userRequest.get(url);
            setRequests(res.data);
        } catch (err) {
            console.error("Error fetching skip 50% requests:", err);
            alert("Failed to fetch skip 50% requests");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (requestId) => {
        if (
            !window.confirm(
                "Are you sure you want to approve this Skip 50% request? The order's 50% deposit requirement will be waived."
            )
        ) {
            return;
        }

        try {
            const res = await userRequest.put(`/skip50Requests/approve/${requestId}`, {
                approvedBy: currentUser._id,
            });

            if (res.data) {
                alert("Skip 50% request approved successfully!");
                fetchRequests();
                setShowModal(false);
                window.dispatchEvent(new Event("skip50RequestsUpdated"));
            }
        } catch (err) {
            console.error("Error approving skip 50% request:", err);
            alert(
                err?.response?.data?.message ||
                    "Failed to approve skip 50% request. Please try again."
            );
        }
    };

    const handleReject = async (requestId) => {
        try {
            const res = await userRequest.put(`/skip50Requests/reject/${requestId}`, {
                rejectedBy: currentUser._id,
            });

            if (res.data) {
                alert("Skip 50% request rejected.");
                fetchRequests();
                setShowModal(false);
                window.dispatchEvent(new Event("skip50RequestsUpdated"));
            }
        } catch (err) {
            console.error("Error rejecting skip 50% request:", err);
            alert("Failed to reject skip 50% request. Please try again.");
        }
    };

    const handleDelete = async (requestId) => {
        if (!window.confirm("Delete this Skip 50% request? This cannot be undone.")) {
            return;
        }
        try {
            await userRequest.delete(`/skip50Requests/${requestId}`);
            alert("Skip 50% request deleted.");
            fetchRequests();
            setShowModal(false);
            window.dispatchEvent(new Event("skip50RequestsUpdated"));
        } catch (err) {
            console.error("Error deleting skip 50% request:", err);
            alert("Failed to delete skip 50% request.");
        }
    };

    const openModal = (request) => {
        setSelectedRequest(request);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRequest(null);
    };

    const getStatusText = (status) => {
        const normalized = String(status || "pending").toLowerCase();
        if (normalized === "approved") return "Approved";
        if (normalized === "rejected") return "Rejected";
        return "Pending";
    };

    const formatCurrency = (amount) =>
        `RM ${parseFloat(amount || 0).toFixed(2)}`;

    if (!canApprove) {
        return (
            <div className="container mx-auto px-2 sm:px-4 py-8 block">
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">
                        Access Denied
                    </h2>
                    <p className="text-gray-700">
                        Only Superadmin, Outright, Manager, and authorized users can access this page.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-2 sm:px-4 py-8 block">
            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-2 sm:p-4 mb-6">
                <div className="flex flex-wrap gap-3">
                    {["pending", "approved", "rejected", ""].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setFilterStatus(s)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                filterStatus === s
                                    ? "bg-orange-400 text-white"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                            {s
                                ? s.charAt(0).toUpperCase() + s.slice(1)
                                : "All"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto orders-table-wrapper">
                <table className="min-w-[1200px] divide-y divide-gray-200 orders-table w-full">
                    <thead className="bg-orange-400">
                        <tr>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Request Date
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Invoice ID
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                CO Number
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Order Total
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Requested By
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan="7"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : requests.length === 0 ? (
                            <tr>
                                <td
                                    colSpan="7"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    No skip 50% requests found.
                                </td>
                            </tr>
                        ) : (
                            requests.map((request) => (
                                <tr
                                    key={request._id}
                                    className={`${
                                        request.status === "pending"
                                            ? "bg-yellow-200 hover:bg-yellow-100"
                                            : request.status === "approved"
                                            ? "bg-green-200 hover:bg-green-100"
                                            : request.status === "rejected"
                                            ? "bg-red-200 hover:bg-red-100"
                                            : "hover:bg-gray-100"
                                    } transition-colors duration-200 cursor-pointer`}
                                    onClick={() => openModal(request)}
                                >
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {moment(request.requestedDate).format(
                                            "DD/MM/YYYY"
                                        )}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.order?.invoiceId || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.order?.coNumber || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.order?.userId?.name ||
                                            request.user?.name ||
                                            "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm font-semibold text-blue-600">
                                        {formatCurrency(request.order?.amount)}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                        {getStatusText(request.status)}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.requestedBy?.name || "Unknown"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                        <div className="flex items-center gap-2">
                                            <Eye
                                                className="pointer icon action-icon-view text-blue-500 hover:text-blue-700"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openModal(request);
                                                }}
                                                titleAccess="View Details"
                                            />
                                            {canApprove &&
                                                request.status === "pending" && (
                                                    <>
                                                        <CheckCircle
                                                            className="pointer icon action-icon-approve text-green-500 hover:text-green-700"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleApprove(
                                                                    request._id
                                                                );
                                                            }}
                                                            titleAccess="Approve"
                                                        />
                                                        <XCircle
                                                            className="pointer icon action-icon-reject text-red-500 hover:text-red-700"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleReject(
                                                                    request._id
                                                                );
                                                            }}
                                                            titleAccess="Reject"
                                                        />
                                                    </>
                                                )}
                                            {superadmin && (
                                                <Delete
                                                    className="pointer icon action-icon-delete text-gray-500 hover:text-gray-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(request._id);
                                                    }}
                                                    titleAccess="Delete"
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && selectedRequest && (
                <div className="overlayContainer installment-modal">
                    <div className="center installment-modal__dialog">
                        <div className="close" onClick={closeModal}>
                            <Close />
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle text-xl font-semibold mb-4">
                                    Skip 50% Request Details
                                </h3>
                                <div className="flex flex-col gap-4 mb-4">
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Status:
                                        </span>
                                        <span>
                                            {getStatusText(selectedRequest.status)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Request Date:
                                        </span>
                                        <span className="text-gray-900">
                                            {moment(
                                                selectedRequest.requestedDate
                                            ).format("DD/MM/YYYY HH:mm:ss")}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Invoice ID:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedRequest.order?.invoiceId ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Customer:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedRequest.order?.userId?.name ||
                                                selectedRequest.user?.name ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Order Total:
                                        </span>
                                        <span className="text-gray-900">
                                            {formatCurrency(
                                                selectedRequest.order?.amount
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Order Deposit:
                                        </span>
                                        <span className="text-gray-900">
                                            {formatCurrency(
                                                selectedRequest.order?.deposit
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Skip 50% Currently:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedRequest.order?.skip50Percent
                                                ? "Yes"
                                                : "No"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Requested By:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedRequest.requestedBy?.name ||
                                                "Unknown"}
                                        </span>
                                    </div>
                                    {selectedRequest.status === "approved" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Approved By:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedRequest.approvedBy
                                                        ?.name || "Unknown"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Approved Date:
                                                </span>
                                                <span className="text-gray-900">
                                                    {moment(
                                                        selectedRequest.approvedDate
                                                    ).format(
                                                        "DD/MM/YYYY HH:mm:ss"
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {selectedRequest.status === "rejected" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Rejected By:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedRequest.rejectedBy
                                                        ?.name || "Unknown"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Rejected Date:
                                                </span>
                                                <span className="text-gray-900">
                                                    {moment(
                                                        selectedRequest.rejectedDate
                                                    ).format(
                                                        "DD/MM/YYYY HH:mm:ss"
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {selectedRequest.remarks && (
                                        <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                            <span className="font-medium text-gray-700">
                                                Remarks:
                                            </span>
                                            <span className="text-gray-900">
                                                {selectedRequest.remarks}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {canApprove &&
                                    selectedRequest.status === "pending" && (
                                        <div className="buttonContainer">
                                            <button
                                                className="confirm pointer green"
                                                onClick={() =>
                                                    handleApprove(
                                                        selectedRequest._id
                                                    )
                                                }
                                            >
                                                Approve Request
                                            </button>
                                            <button
                                                className="cancel pointer red"
                                                onClick={() =>
                                                    handleReject(
                                                        selectedRequest._id
                                                    )
                                                }
                                            >
                                                Reject Request
                                            </button>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Skip50Requests;
